import React, { useState, useEffect } from 'react';
import { Form, Select, Input, InputNumber, Row, Col, Card, Button, Spin, message, Modal, Space, Tag, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingOutlined, SearchOutlined, CloseOutlined, ArrowLeftOutlined, AppstoreOutlined, BorderOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';

const { Title } = Typography;

const { Option } = Select;

interface Producto {
  id: number;
  descripcion: string;
  tipo: number;
  nivel: string;
}

interface TipoCosecha {
  id: number;
  descripcion?: string;
  valor_string?: string;
}

interface VariedadSemilla {
  id: number;
  descripcion?: string;
  valor_string?: string;
}

interface Presentacion {
  id: number;
  descripcion: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
}

interface Padron {
  id: number;
  nc: string;
  denominacion: string;
}

interface Persona {
  id_padron: number;
  nc: string;
  denominacion: string;
}

interface Establecimiento {
  id: number;
  descripcion: string;
}

interface Lote {
  id: number;
  descripcion: string;
}

interface Chofer {
  id: number;
  nombre: string;
  vencimiento_carnet: string;
  vencimiento_art: string;
}

interface Camion {
  id: number;
  patente_camion: string;
  patente_acoplado: string;
}

interface PesajeForm {
  id_empresa: number;
  motivo: string;
  sucursal?: number;
  producto: number;
  tipo_cosecha?: number;
  tipo_semilla?: number;
  presentacion?: number;
  cliente: number;
  productor?: number;
  establecimiento?: number;
  lote?: number;
  transportista: number;
  chofer: number;
  camion: number;
  tarifa: number;
  humedad?: number;
  cantidad_rollos?: number;
  ubicacion_playa?: string;
  calificacion?: string;
  remito?: string;
  primera_pasada?: number;
}

interface User {
  id_empresa: number;
  id_sucursal: number;
}

interface DatosFijos {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  valor_string: string;
  alta: number;
}

interface CeldaResponse {
  casillero: string;
  error?: string;
}

interface BalanzaData {
  id: number;
  ingreso_egreso: string;
  compra_servicio: string;
  producto: number | null;
  cosecha: number | null;
  variedad_semilla: number | null;
  presentacion: number | null;
  dtv: string | null;
  id_aotrasucursal: number | null;
  cliente: number | null;
  productor: number | null;
  establecimiento: number | null;
  nlote: number | null;
  transportista: number | null;
  chofer: number | null;
  camion: number | null;
  tarifa: number | null;
  kilos_primera: number | null;
  humedad: number | null;
  cantidad_rollos: number | null;
  ubicacion: string | null;
  puntaje_presentacion: string | null;
  numero_remito: string | null;
}

interface RolloData {
  id: number;
  rollo_identificacion: string;
  balanza: number;
  id_empresa: number;
  id_sucursal: number;
  alta: number;
}

const EditarBalanza: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const [form] = Form.useForm();
  const { id_empresa: idEmpresaParam, id_sucursal: idSucursalParam, registroId } = useParams<{
    id_empresa: string;
    id_sucursal: string;
    registroId: string;
  }>();

  const userMetadata = user as unknown as User | undefined;
  const id_empresa = idEmpresaParam ? Number(idEmpresaParam) : userMetadata?.id_empresa;
  const id_sucursal = idSucursalParam ? Number(idSucursalParam) : userMetadata?.id_sucursal;
  const registroIdNumber = registroId ? Number(registroId) : undefined;

  const [productos, setProductos] = useState<Producto[]>([]);
  const [tiposCosecha, setTiposCosecha] = useState<TipoCosecha[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tiposSemilla, setTiposSemilla] = useState<VariedadSemilla[]>([]);
  const [presentaciones, setPresentaciones] = useState<Presentacion[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [clientes, setClientes] = useState<Padron[]>([]);
  const [productores, setProductores] = useState<Padron[]>([]);
  const [transportistas, setTransportistas] = useState<Padron[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [choferes, setChoferes] = useState<Chofer[]>([]);
  const [camiones, setCamiones] = useState<Camion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnulando, setIsAnulando] = useState(false);
  const [estadoCalificacion, setEstadoCalificacion] = useState<boolean>(false);
  const [calificacionEnabled, setCalificacionEnabled] = useState<boolean>(false);
  const [tipoCosechaEnabled, setTipoCosechaEnabled] = useState<boolean>(false);
  const [tipoSemillaEnabled, setTipoSemillaEnabled] = useState<boolean>(false);
  const [presentacionEnabled, setPresentacionEnabled] = useState<boolean>(false);
  const [playa, setPlaya] = useState<DatosFijos[]>([]);
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Persona | null>(null);
  const [searchProductor, setSearchProductor] = useState('');
  const [showSearchProductor, setShowSearchProductor] = useState(false);
  const [selectedProductor, setSelectedProductor] = useState<Persona | null>(null);
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [selectedTransportista, setSelectedTransportista] = useState<Persona | null>(null);
  const [rollosAsignados, setRollosAsignados] = useState<RolloData[]>([]);
  const [registroOriginal, setRegistroOriginal] = useState<BalanzaData | null>(null);
  
  // Estado para número de columnas (3 o 4)
  const [numColumnas, setNumColumnas] = useState<number>(() => {
    const saved = localStorage.getItem('editar_balanza_columnas');
    return saved ? Number(saved) : 4; // Por defecto 4 columnas
  });

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
  
  // Función para cambiar número de columnas
  const toggleColumnas = () => {
    const nuevoNum = numColumnas === 3 ? 4 : 3;
    setNumColumnas(nuevoNum);
    localStorage.setItem('editar_balanza_columnas', nuevoNum.toString());
  };

  const verificarEstadoCalificacion = async () => {
    try {
      if (!id_empresa || !id_sucursal) {
        console.error('No se encontraron parámetros de empresa o sucursal');
        return false;
      }

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PRE`,
        { token }
      );

      const tieneEstadoActivo =
        response.data && response.data.length > 0 && response.data[0].alta === 1;

      return tieneEstadoActivo;
    } catch (error) {
      console.error('Error al verificar estado de calificación:', error);
      return false;
    }
  };

  const fetchProductos = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/productos`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar productos');
      }

      const data = await response.json();
      setProductos(data);
      return data as Producto[];
    } catch (error) {
      console.error('Error al cargar productos:', error);
      message.error('No se pudieron cargar los productos');
      return [];
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown')) {
        setShowSearchCliente(false);
        setShowSearchProductor(false);
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTiposCosecha = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/tipo_cosecha`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar tipos de cosecha');
      const data = await response.json();
      setTiposCosecha(data);
      return data as TipoCosecha[];
    } catch (error) {
      console.error('Error al cargar tipos de cosecha:', error);
      message.error('Error al cargar tipos de cosecha');
      return [];
    }
  };

  const fetchVariedadesSemilla = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/variedad_semilla`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar variedades de semilla');
      const data = await response.json();
      setTiposSemilla(data);
      return data as VariedadSemilla[];
    } catch (error) {
      console.error('Error al cargar variedades de semilla:', error);
      message.error('Error al cargar variedades de semilla');
      return [];
    }
  };

  const fetchPresentaciones = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/presentacion`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar presentaciones');
      const data = await response.json();
      setPresentaciones(data);
      return data as Presentacion[];
    } catch (error) {
      console.error('Error al cargar presentaciones:', error);
      message.error('Error al cargar presentaciones');
      return [];
    }
  };

  const fetchSucursales = async () => {
    try {
      if (!id_empresa) {
        return [];
      }
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_SUCURSAL_URL}/sucursales/${id_empresa}`;
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar sucursales');
      }

      const data = await response.json();
      setSucursales(data);
      return data as Sucursal[];
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
      return [];
    }
  };

  const fetchPadron = async () => {
    try {
      if (!id_empresa) {
        return [];
      }

      const token = await getAccessTokenSilently();

      const urlUsuarios = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}/`;

      const responseUsuarios = await fetch(urlUsuarios, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (responseUsuarios.ok) {
        const dataUsuarios = await responseUsuarios.json();
        setClientes(dataUsuarios);
        setProductores(dataUsuarios);
        setTransportistas(dataUsuarios);
        return dataUsuarios as Padron[];
      } else {
        console.error('Error al cargar el padrón - Status:', responseUsuarios.status);
        return [];
      }
    } catch (error) {
      console.error('Error en fetchPadron:', error);
      return [];
    }
  };

  const fetchEstablecimientos = async (productorId: number) => {
    try {
      if (!id_empresa) return [];
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/establecimiento/${id_empresa}/${productorId}`;
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar establecimientos');
      }

      const data = await response.json();
      setEstablecimientos(data);
      return data as Establecimiento[];
    } catch (error) {
      console.error('Error al cargar establecimientos:', error);
      setEstablecimientos([]);
      return [];
    }
  };

  const fetchLotes = async (productorId: number, establecimientoId: number) => {
    try {
      if (!id_empresa) return [];
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/lote/${id_empresa}/${establecimientoId}/`;

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar lotes: ${response.status}`);
      }

      const data = await response.json();
      setLotes(data);
      return data as Lote[];
    } catch (error) {
      console.error('Error al cargar lotes:', error);
      setLotes([]);
      return [];
    }
  };

  const fetchChoferes = async (transportistaId: number) => {
    try {
      if (!id_empresa) return [];
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/chofer/${id_empresa}/${transportistaId}/`;

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar choferes: ${response.status}`);
      }

      const data = await response.json();
      setChoferes(data);
      return data as Chofer[];
    } catch (error) {
      console.error('Error al cargar choferes:', error);
      setChoferes([]);
      return [];
    }
  };

  const fetchCamiones = async (transportistaId: number) => {
    try {
      if (!id_empresa) return [];
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/camion/${id_empresa}/${transportistaId}/`;

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar camiones: ${response.status}`);
      }

      const data = await response.json();
      setCamiones(data);
      return data as Camion[];
    } catch (error) {
      console.error('Error al cargar camiones:', error);
      setCamiones([]);
      return [];
    }
  };

  const handleProductoChange = async (value: number) => {
    try {
      const productoSeleccionado = productos.find((p) => p.id === value);

      setCalificacionEnabled(false);
      setTipoCosechaEnabled(false);
      setTipoSemillaEnabled(false);
      setPresentacionEnabled(false);

      if (productoSeleccionado) {
        if (productoSeleccionado.tipo === 1) {
          const ec = await verificarEstadoCalificacion();
          setEstadoCalificacion(ec);

          await Promise.all([fetchTiposCosecha(), fetchVariedadesSemilla(), fetchPresentaciones()]);

          if (ec) {
            setCalificacionEnabled(true);
            setTipoCosechaEnabled(true);
            setTipoSemillaEnabled(true);
            setPresentacionEnabled(true);
          } else {
            form.setFieldsValue({
              calificacion: undefined,
              tipo_cosecha: undefined,
              tipo_semilla: undefined,
              presentacion: undefined,
            });
          }
        } else {
          setEstadoCalificacion(false);
          form.setFieldsValue({
            calificacion: undefined,
            tipo_cosecha: undefined,
            tipo_semilla: undefined,
            presentacion: undefined,
          });
        }
      }
    } catch (error) {
      console.error('Error en handleProductoChange:', error);
      message.error('Error al procesar el producto seleccionado');
      setEstadoCalificacion(false);
      setCalificacionEnabled(false);
      setTipoCosechaEnabled(false);
      setTipoSemillaEnabled(false);
      setPresentacionEnabled(false);
    }
  };

  const handleMotivoChange = (value: string) => {
    form.setFieldsValue({
      sucursal: undefined,
      cliente: undefined,
      productor: undefined,
      transportista: undefined,
      chofer: undefined,
      camion: undefined,
    });

    if (value === 'F' || value === 'A') {
      fetchSucursales();
    }

    if (value === 'A') {
      fetchPadron();
    }
  };

  const handleProductorChange = (value: number) => {
    form.setFieldValue('establecimiento', undefined);
    if (value) {
      fetchEstablecimientos(value);
    }
  };

  const handleEstablecimientoChange = (value: number) => {
    form.setFieldValue('lote', undefined);
    const productorId = form.getFieldValue('productor');
    if (value && productorId) {
      fetchLotes(productorId, value);
    }
  };

  const handleTransportistaChange = (value: number) => {
    form.setFieldsValue({
      chofer: undefined,
      camion: undefined,
    });

    if (value) {
      fetchChoferes(value);
      fetchCamiones(value);
    } else {
      setChoferes([]);
      setCamiones([]);
    }
  };

  const fetchDatosPlaya = async () => {
    try {
      if (!id_empresa || !id_sucursal) {
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PMR`,
        { token }
      );

      setPlaya(response.data);
    } catch (error) {
      console.error('Error al obtener datos de playa:', error);
    }
  };

  const verificarEstadoPlaya = () => {
    if (playa && playa.length > 0) {
      if (playa[0].alta === 1) {
        return true;
      }
      return false;
    }
    return false;
  };

  const buscarCeldaDesocupada = async (clienteId: number) => {
    try {
      if (!verificarEstadoPlaya()) {
        return;
      }

      const [dato_playa_columnas, dato_playa_filas] = playa[0].valor_string.split('=').map((item) => item.trim());
      const [desde_columna, hasta_columna] = dato_playa_columnas.split('-').map((item) => item.trim());
      const [desde_linea, hasta_linea] = dato_playa_filas.split('-').map((item) => item.trim());

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<CeldaResponse>(
        `${process.env.REACT_APP_RELACION_URL}/celdadesocupada/${clienteId}/${desde_linea}/${hasta_linea}/${desde_columna}/${hasta_columna}/${id_empresa}/${id_sucursal}`,
        { token }
      );

      const casillero = response.data.casillero;
      form.setFieldValue('ubicacion_playa', casillero);
      return casillero;
    } catch (error) {
      console.error('Error al buscar celda desocupada:', error);
      message.error('Error al buscar ubicación en playa disponible');
      return null;
    }
  };

  const fetchRegistroBalanza = async (): Promise<BalanzaData | null> => {
    try {
      if (!id_empresa || !id_sucursal || !registroIdNumber) {
        return null;
      }
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/editar_balanza/${id_empresa}/${id_sucursal}/${registroIdNumber}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener el registro de balanza');
      }

      const data = await response.json();
      if (data.status !== 'success') {
        message.error('No se encontró el registro solicitado');
        return null;
      }

      setRollosAsignados(data.rollos || []);
      return data.data as BalanzaData;
    } catch (error) {
      console.error('Error al cargar el registro de balanza:', error);
      message.error('No se pudo cargar el registro de balanza');
      return null;
    }
  };

  const hydrateForm = async (registro: BalanzaData, showLoading = false, padronList: Padron[] = [], productosList: Producto[] = []) => {
    if (!registro) return;
    try {
      if (showLoading) {
        setLoading(true);
      }

      // Usar listas frescas pasadas como parámetro para evitar stale closure
      const padronToUse = padronList.length > 0 ? padronList : clientes;
      const productosToUse = productosList.length > 0 ? productosList : productos;

      if (registro.compra_servicio === 'A' || registro.compra_servicio === 'F') {
        await fetchSucursales();
      }

      if (registro.producto) {
        form.setFieldValue('producto', registro.producto);
        const productoSeleccionado = productosToUse.find(p => p.id === registro.producto);
        if (productoSeleccionado?.tipo === 1) {
          const ec = await verificarEstadoCalificacion();
          setEstadoCalificacion(ec);
          await Promise.all([fetchTiposCosecha(), fetchVariedadesSemilla(), fetchPresentaciones()]);
          setCalificacionEnabled(ec);
          setTipoCosechaEnabled(ec);
          setTipoSemillaEnabled(ec);
          setPresentacionEnabled(ec);
        } else {
          setCalificacionEnabled(false);
          setTipoCosechaEnabled(false);
          setTipoSemillaEnabled(false);
          setPresentacionEnabled(false);
        }
      } else {
        form.setFieldValue('producto', undefined);
      }

      if (registro.productor) {
        await fetchEstablecimientos(registro.productor);
        if (registro.establecimiento) {
          await fetchLotes(registro.productor, registro.establecimiento);
        } else {
          setLotes([]);
        }
      } else {
        setEstablecimientos([]);
        setLotes([]);
      }

      if (registro.transportista) {
        await Promise.all([
          fetchChoferes(registro.transportista),
          fetchCamiones(registro.transportista),
        ]);
      } else {
        setChoferes([]);
        setCamiones([]);
      }

      const clienteSeleccionado = padronToUse.find((c) => c.id === registro.cliente);
      if (clienteSeleccionado) {
        setSelectedCliente({
          id_padron: clienteSeleccionado.id,
          nc: clienteSeleccionado.nc,
          denominacion: clienteSeleccionado.denominacion,
        });
      } else {
        setSelectedCliente(null);
      }

      const productorSeleccionado = padronToUse.find((p) => p.id === registro.productor);
      if (productorSeleccionado) {
        setSelectedProductor({
          id_padron: productorSeleccionado.id,
          nc: productorSeleccionado.nc,
          denominacion: productorSeleccionado.denominacion,
        });
      } else {
        setSelectedProductor(null);
      }

      const transportistaSeleccionado = padronToUse.find((t) => t.id === registro.transportista);
      if (transportistaSeleccionado) {
        setSelectedTransportista({
          id_padron: transportistaSeleccionado.id,
          nc: transportistaSeleccionado.nc,
          denominacion: transportistaSeleccionado.denominacion,
        });
      } else {
        setSelectedTransportista(null);
      }

      form.setFieldsValue({
        tipo_movimiento: registro.ingreso_egreso || undefined,
        motivo: registro.compra_servicio || undefined,
        producto: registro.producto || undefined,
        tipo_cosecha: registro.cosecha || undefined,
        tipo_semilla: registro.variedad_semilla || undefined,
        presentacion: registro.presentacion || undefined,
        dtv: registro.dtv || undefined,
        sucursal: registro.id_aotrasucursal || undefined,
        cliente: registro.cliente || undefined,
        productor: registro.productor || undefined,
        establecimiento: registro.establecimiento || undefined,
        lote: registro.nlote || undefined,
        transportista: registro.transportista || undefined,
        chofer: registro.chofer || undefined,
        camion: registro.camion || undefined,
        tarifa: registro.tarifa ?? undefined,
        humedad: registro.humedad ?? undefined,
        cantidad_rollos: registro.cantidad_rollos ?? undefined,
        ubicacion_playa: registro.ubicacion || undefined,
        calificacion: registro.puntaje_presentacion && /^[1-5]{3}$/.test(registro.puntaje_presentacion) ? registro.puntaje_presentacion : undefined,
        remito: registro.numero_remito || undefined,
        primera_pasada: registro.kilos_primera ?? undefined,
      });

      setRegistroOriginal(registro);
    } catch (error) {
      console.error('Error al preparar el formulario:', error);
      message.error('No se pudieron aplicar los datos del registro');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!id_empresa || !id_sucursal || !registroIdNumber) {
        message.error('Faltan parámetros para editar la balanza');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const productosData = await fetchProductos();
        const [padronData] = await Promise.all([fetchPadron(), fetchDatosPlaya()]);

        const registro = await fetchRegistroBalanza();
        if (registro) {
          await hydrateForm(registro, false, padronData || [], productosData || []);
        }
      } catch (error) {
        console.error('Error al inicializar EditarBalanza:', error);
        message.error('Error al cargar la información inicial');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id_empresa, id_sucursal, registroIdNumber, getAccessTokenSilently]);

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin indicator={antIcon} size="large" tip="Cargando datos..." />
        </div>
      </Card>
    );
  }

  const handleAnularTicket = () => {
    Modal.confirm({
      title: 'Anular ticket de balanza',
      content: '¿Está seguro que desea anular este ticket? Esta acción no se puede deshacer.',
      okText: 'Sí, anular',
      okButtonProps: { danger: true },
      cancelText: 'No, cancelar',
      onOk: async () => {
        if (!id_empresa || !id_sucursal || !registroIdNumber) return;
        setIsAnulando(true);
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(
            `${process.env.REACT_APP_BALANZA_URL}/anular_balanza/${id_empresa}/${id_sucursal}/${registroIdNumber}/`,
            { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          const data = await response.json();
          if (!response.ok || data.status !== 'success') {
            message.error(data.message || 'No se pudo anular el ticket');
            return;
          }
          message.success('Ticket anulado exitosamente');
          navigate('/listar_pesaje');
        } catch {
          message.error('Error al anular el ticket');
        } finally {
          setIsAnulando(false);
        }
      },
    });
  };

  const onFinish = async (values: any) => {
    if (!id_empresa || !id_sucursal || !registroIdNumber) {
      message.error('No es posible actualizar el registro. Faltan parámetros.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();

      const pesajeData: PesajeForm = {
        id_empresa,
        id_sucursal,
        ...values,
        tarifa: Number(values.tarifa),
        humedad: values.humedad !== undefined ? Number(values.humedad) : undefined,
        cantidad_rollos: values.cantidad_rollos !== undefined ? Number(values.cantidad_rollos) : undefined,
        primera_pasada: values.primera_pasada !== undefined ? Number(values.primera_pasada) : undefined,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/actualizar_balanza/${id_empresa}/${id_sucursal}/${registroIdNumber}/`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(pesajeData),
        }
      );

      if (!response.ok) {
        throw new Error(`Error al actualizar pesaje: ${response.status}`);
      }

      const data = await response.json();
      if (data.status !== 'success') {
        message.error(data.message || 'No se pudo actualizar el pesaje');
        return;
      }

      message.success('Pesaje actualizado exitosamente');
      navigate('/listar_pesaje');
    } catch (error) {
      console.error('Error al actualizar pesaje:', error);
      message.error('Error al actualizar el pesaje');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Cargando datos del pesaje..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página con botón de volver y toggle de columnas */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: '12px', 
        marginBottom: '12px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/listar_pesaje')}
            size="small"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: '6px',
              height: '32px',
              fontSize: '13px'
            }}
          >
            Volver
          </Button>
          <Title
            level={3} 
            style={{ 
              margin: 0,
              color: theme.colors.primaryDark, 
              fontSize: '20px', 
              fontWeight: 600,
              lineHeight: '1.2'
            }}
          >
            Editar Pesaje
          </Title>
        </div>
        <Button
          icon={numColumnas === 4 ? <AppstoreOutlined /> : <BorderOutlined />}
          onClick={toggleColumnas}
          size="small"
          type={numColumnas === 4 ? 'primary' : 'default'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
            fontSize: '13px',
            minWidth: '100px'
          }}
          title={`Cambiar a ${numColumnas === 4 ? '3' : '4'} columnas`}
        >
          {numColumnas === 4 ? '4 Columnas' : '3 Columnas'}
        </Button>
      </div>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '1400px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 16px',
            minHeight: 'auto'
          }
        }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Información del Pesaje</span>
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              size="small"
              onClick={handleAnularTicket}
              loading={isAnulando}
              disabled={isSubmitting}
            >
              Anular ticket
            </Button>
          </div>
        }
      >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={onFinish}
        style={{ 
          maxWidth: '100%',
          padding: '12px'
        }}
        className="pesaje-form"
      >
        <style>{`
          .pesaje-form .ant-form-item-label > label {
            color: #374151 !important;
            font-weight: 500 !important;
            font-size: 12px !important;
          }
          .pesaje-form .ant-form-item-required::before {
            color: ${theme.colors.error} !important;
          }
          .pesaje-form .ant-input,
          .pesaje-form .ant-select-selector,
          .pesaje-form .ant-input-number {
            border-color: #d1d5db !important;
            border-radius: 6px !important;
            font-size: 13px !important;
          }
          .pesaje-form .ant-input:focus,
          .pesaje-form .ant-select-selector:focus,
          .pesaje-form .ant-input-number:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
          .pesaje-grid {
            display: grid;
            gap: 12px;
          }
          .pesaje-grid.columns-4 {
            grid-template-columns: repeat(4, 1fr);
          }
          .pesaje-grid.columns-3 {
            grid-template-columns: repeat(3, 1fr);
          }
          @media (max-width: 1200px) {
            .pesaje-grid.columns-4 {
              grid-template-columns: repeat(3, 1fr);
            }
            .pesaje-grid.columns-3 {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 768px) {
            .pesaje-grid.columns-4,
            .pesaje-grid.columns-3 {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        
        {/* Grid responsive - 3 o 4 columnas según preferencia del usuario */}
        <div className={`pesaje-grid ${numColumnas === 4 ? 'columns-4' : 'columns-3'}`}>
          
          {/* Tipo de Movimiento */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Tipo de Movimiento:
              </label>
              <Form.Item name="tipo_movimiento" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Seleccione tipo"
                  size="middle"
                  onChange={() => {
                    form.setFieldValue('motivo', undefined);
                  }}
                >
                  <Option value="I">Ingreso</Option>
                  <Option value="E">Egreso</Option>
                </Select>
              </Form.Item>
            </div>

          {/* Tipo Semilla */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.tipo_movimiento !== currentValues.tipo_movimiento}
            >
              {({ getFieldValue }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: theme.colors.textPrimary,
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    marginBottom: 0,
                    lineHeight: '1.2'
                  }}>
                    Motivo:
                  </label>
                  <Form.Item name="motivo" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                    <Select
                      placeholder="Seleccione motivo"
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                      disabled={!getFieldValue('tipo_movimiento')}
                      onChange={handleMotivoChange}
                    >
                    {getFieldValue('tipo_movimiento') === 'I' ? (
                      <>
                        <Option value="C">Compra</Option>
                        <Option value="S">Servicio</Option>
                        <Option value="R">Recupero</Option>
                        <Option value="F">Desde Otra Sucursal</Option>
                      </>
                    ) : (
                      <>
                        <Option value="V">Venta</Option>
                        <Option value="A">A Otra Sucursal</Option>
                        <Option value="D">Cambio Dueño</Option>
                        <Option value="E">Retiro Cliente</Option>
                      </>
                    )}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </Form.Item>

          {/* Producto */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Producto:
              </label>
              <Form.Item name="producto" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Seleccione producto"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleProductoChange}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                {productos.map((producto) => (
                  <Option key={producto.id} value={producto.id}>
                    {producto.descripcion}
                  </Option>
                ))}
                </Select>
              </Form.Item>
            </div>

          {/* Tipo de Cosecha */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Tipo de Cosecha:
                    </label>
                    <Form.Item
                      name="tipo_cosecha"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={camposHabilitados ? 'Seleccione tipo de cosecha' : 'No disponible'}
                        size="middle"
                        disabled={!camposHabilitados}
                        optionFilterProp="children"
                      >
                      {tiposCosecha.map((tipo) => (
                        <Option key={tipo.id} value={tipo.id}>
                          {tipo.valor_string ?? tipo.descripcion ?? tipo.id}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Tipo Semilla */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const semillaHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Tipo Semilla:
                    </label>
                    <Form.Item
                      name="tipo_semilla"
                      rules={[{ required: semillaHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={semillaHabilitado ? 'Seleccione tipo de semilla' : 'No disponible'}
                        size="middle"
                        disabled={!semillaHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                        {semillaHabilitado &&
                          tiposSemilla.map((tipo) => (
                            <Option key={tipo.id} value={tipo.id}>
                              {tipo.valor_string ?? tipo.descripcion ?? tipo.id}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Presentación */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const presentacionHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Presentación:
                    </label>
                    <Form.Item
                      name="presentacion"
                      rules={[{ required: presentacionHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={presentacionHabilitado ? 'Seleccione presentación' : 'No disponible'}
                        size="middle"
                        disabled={!presentacionHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                        {presentacionHabilitado &&
                          presentaciones.map((presentacion) => (
                            <Option key={presentacion.id} value={presentacion.id}>
                              {presentacion.descripcion}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Presentación */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                DTV:
              </label>
              <Form.Item name="dtv" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Input placeholder="Ingrese DTV" size="middle" maxLength={50} />
              </Form.Item>
            </div>

          {/* Sucursal */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.motivo !== currentValues.motivo}
            >
              {({ getFieldValue }) => {
                const motivoSeleccionado = getFieldValue('motivo');
                const sucursalHabilitado = motivoSeleccionado === 'A' || motivoSeleccionado === 'F';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Sucursal:
                    </label>
                    <Form.Item
                      name="sucursal"
                      rules={[{ required: sucursalHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={sucursalHabilitado ? 'Seleccione sucursal' : 'No disponible'}
                        size="middle"
                        showSearch
                        disabled={!sucursalHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                        {sucursalHabilitado &&
                          sucursales
                            .filter((suc) => {
                              if (motivoSeleccionado === 'A') {
                                return suc.id !== id_sucursal;
                              }
                              return true;
                            })
                            .map((sucursal) => (
                              <Option key={sucursal.id} value={sucursal.id}>
                                {sucursal.denominacion}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Cliente */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.motivo !== currentValues.motivo}
            >
              {({ getFieldValue }) => {
                const motivoSeleccionado = getFieldValue('motivo');
                const clienteHabilitado = motivoSeleccionado !== 'A';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Cliente:
                    </label>
                    <Form.Item name="cliente" rules={[{ required: clienteHabilitado, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input
                        type="text"
                        placeholder={clienteHabilitado ? 'Buscar por NC o denominación...' : 'No disponible'}
                        required={clienteHabilitado}
                        disabled={!clienteHabilitado}
                        style={{
                          width: '100%',
                          paddingLeft: '28px',
                          paddingRight: selectedCliente ? '28px' : undefined,
                          paddingTop: '5px',
                          paddingBottom: '5px',
                          fontSize: '13px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          height: '32px',
                          backgroundColor: selectedCliente ? '#f5f5f5' : !clienteHabilitado ? '#f0f0f0' : 'white',
                          cursor: clienteHabilitado ? 'pointer' : 'not-allowed',
                        }}
                        value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
                        onChange={(e) => {
                          setSearchCliente(e.target.value);
                          setSelectedCliente(null);
                          setShowSearchCliente(true);
                        }}
                        onFocus={() => setShowSearchCliente(true)}
                      />
                      <SearchOutlined
                        style={{
                          position: 'absolute',
                          left: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666',
                          pointerEvents: 'none',
                          fontSize: '13px',
                        }}
                      />
                      {selectedCliente && (
                        <CloseOutlined
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '13px',
                            zIndex: 1100,
                          }}
                          onClick={() => {
                            setSelectedCliente(null);
                            setSearchCliente('');
                            setShowSearchCliente(true);
                            form.setFieldValue('cliente', undefined);
                          }}
                          title="Limpiar selección"
                        />
                      )}
                      {showSearchCliente && clientes.length > 0 && !selectedCliente && (
                        <div
                          className="search-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '400px',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {clientes
                            .filter((c) => {
                              const search = searchCliente.trim().toLowerCase();
                              return c.nc.toLowerCase().includes(search) || c.denominacion.toLowerCase().includes(search);
                            })
                            .map((cliente) => (
                              <div
                                key={cliente.id}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #eee',
                                  textAlign: 'left',
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                                onClick={async () => {
                                  const personaCliente: Persona = {
                                    id_padron: cliente.id,
                                    nc: cliente.nc,
                                    denominacion: cliente.denominacion,
                                  };
                                  setSelectedCliente(personaCliente);
                                  form.setFieldValue('cliente', cliente.id);
                                  setShowSearchCliente(false);
                                  if (verificarEstadoPlaya()) {
                                    await buscarCeldaDesocupada(cliente.id);
                                  }
                                }}
                              >
                                {cliente.nc} - {cliente.denominacion}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Productor */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.producto !== currentValues.producto || prevValues.tipo_cosecha !== currentValues.tipo_cosecha
              }
            >
              {({ getFieldValue }) => {
                const productoId = getFieldValue('producto');
                const productoSeleccionado = productos.find((p) => p.id === productoId);
                const productorHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Productor:
                    </label>
                    <Form.Item
                      name="productor"
                      rules={[{ required: productorHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input
                        type="text"
                        placeholder={productorHabilitado ? 'Buscar por NC o denominación...' : 'No disponible'}
                        required={productorHabilitado}
                        disabled={!productoId || productoSeleccionado?.tipo !== 1}
                        style={{
                          width: '100%',
                          paddingLeft: '28px',
                          paddingRight: selectedProductor ? '28px' : undefined,
                          paddingTop: '5px',
                          paddingBottom: '5px',
                          fontSize: '13px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          height: '32px',
                          backgroundColor: selectedProductor ? '#f5f5f5' : 'white',
                          cursor: !productoId || productoSeleccionado?.tipo !== 1 ? 'not-allowed' : 'pointer',
                        }}
                        value={selectedProductor ? `${selectedProductor.nc} - ${selectedProductor.denominacion}` : searchProductor}
                        onChange={(e) => {
                          setSearchProductor(e.target.value);
                          setSelectedProductor(null);
                          setShowSearchProductor(true);
                        }}
                        onFocus={() => setShowSearchProductor(true)}
                      />
                      <SearchOutlined
                        style={{
                          position: 'absolute',
                          left: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666',
                          pointerEvents: 'none',
                          fontSize: '13px',
                        }}
                      />
                      {selectedProductor && (
                        <CloseOutlined
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '13px',
                            zIndex: 1100,
                          }}
                          onClick={() => {
                            setSelectedProductor(null);
                            setSearchProductor('');
                            setShowSearchProductor(true);
                            form.setFieldValue('productor', undefined);
                          }}
                          title="Limpiar selección"
                        />
                      )}
                      {showSearchProductor && productores.length > 0 && !selectedProductor && productorHabilitado && (
                        <div
                          className="search-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '400px',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {productores
                            .filter((p) => {
                              const search = searchProductor.trim().toLowerCase();
                              return p.nc.toLowerCase().includes(search) || p.denominacion.toLowerCase().includes(search);
                            })
                            .map((productor) => (
                              <div
                                key={productor.id}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                  textAlign: 'left',
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                                onClick={() => {
                                  const personaProductor: Persona = {
                                    id_padron: productor.id,
                                    nc: productor.nc,
                                    denominacion: productor.denominacion,
                                  };
                                  setSelectedProductor(personaProductor);
                                  form.setFieldValue('productor', productor.id);
                                  setShowSearchProductor(false);
                                  handleProductorChange(productor.id);
                                }}
                              >
                                {productor.nc} - {productor.denominacion}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Establecimiento */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.productor !== currentValues.productor}
            >
              {({ getFieldValue }) => {
                const productorSeleccionado = getFieldValue('productor');
                const establecimientoHabilitado = !!productorSeleccionado;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Establecimiento:
                    </label>
                    <Form.Item
                      name="establecimiento"
                      rules={[{ required: establecimientoHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={establecimientoHabilitado ? 'Seleccione establecimiento' : 'No disponible'}
                        size="middle"
                        showSearch
                        disabled={!establecimientoHabilitado}
                        onChange={handleEstablecimientoChange}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                        {establecimientoHabilitado &&
                          establecimientos.map((establecimiento) => (
                            <Option key={establecimiento.id} value={establecimiento.id}>
                              {establecimiento.descripcion}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Productor */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.establecimiento !== currentValues.establecimiento}
            >
              {({ getFieldValue }) => {
                const establecimientoSeleccionado = getFieldValue('establecimiento');
                const loteHabilitado = !!establecimientoSeleccionado;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Lote:
                    </label>
                    <Form.Item name="lote" rules={[{ required: loteHabilitado, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                      <Select
                        placeholder={loteHabilitado ? 'Seleccione lote' : 'No disponible'}
                        size="middle"
                        showSearch
                        disabled={!loteHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                        {loteHabilitado &&
                          lotes.map((lote) => (
                            <Option key={lote.id} value={lote.id}>
                              {lote.descripcion}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Transportista */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Transportista:
              </label>
              <Form.Item name="transportista" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Buscar por NC o denominación..."
                  required
                  style={{
                    width: '100%',
                    paddingLeft: '28px',
                    paddingRight: selectedTransportista ? '28px' : undefined,
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    fontSize: '13px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    height: '32px',
                    backgroundColor: selectedTransportista ? '#f5f5f5' : 'white',
                    cursor: 'pointer',
                  }}
                  value={selectedTransportista ? `${selectedTransportista.nc} - ${selectedTransportista.denominacion}` : searchTransportista}
                  onChange={(e) => {
                    setSearchTransportista(e.target.value);
                    setSelectedTransportista(null);
                    setShowSearchTransportista(true);
                  }}
                  onFocus={() => setShowSearchTransportista(true)}
                />
                <SearchOutlined
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    pointerEvents: 'none',
                    fontSize: '13px',
                  }}
                />
                {selectedTransportista && (
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      cursor: 'pointer',
                      fontSize: '13px',
                      zIndex: 1100,
                    }}
                    onClick={() => {
                      setSelectedTransportista(null);
                      setSearchTransportista('');
                      setShowSearchTransportista(true);
                      form.setFieldValue('transportista', undefined);
                    }}
                    title="Limpiar selección"
                  />
                )}
                {showSearchTransportista && transportistas.length > 0 && !selectedTransportista && (
                  <div
                    className="search-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '400px',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    {transportistas
                      .filter((t) => {
                        const search = searchTransportista.trim().toLowerCase();
                        return t.nc.toLowerCase().includes(search) || t.denominacion.toLowerCase().includes(search);
                      })
                      .map((transportista) => (
                        <div
                          key={transportista.id}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            textAlign: 'left',
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          onClick={() => {
                            const personaTransportista: Persona = {
                              id_padron: transportista.id,
                              nc: transportista.nc,
                              denominacion: transportista.denominacion,
                            };
                            setSelectedTransportista(personaTransportista);
                            form.setFieldValue('transportista', transportista.id);
                            setShowSearchTransportista(false);
                            handleTransportistaChange(transportista.id);
                          }}
                        >
                          {transportista.nc} - {transportista.denominacion}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              </Form.Item>
            </div>

          {/* Tipo Semilla */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Chofer:
              </label>
              <Form.Item name="chofer" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Seleccione chofer"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  disabled={!selectedTransportista}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {choferes.map((chofer) => (
                    <Option key={chofer.id} value={chofer.id}>
                      {chofer.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

          {/* Camión/Acoplado */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Camión/Acoplado:
              </label>
              <Form.Item name="camion" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Seleccione camión"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  disabled={!selectedTransportista}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {camiones.map((camion) => (
                    <Option key={camion.id} value={camion.id}>
                      {camion.patente_camion}/{camion.patente_acoplado}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

          {/* Tarifa $/Km */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Tarifa $/Km:
              </label>
              <Form.Item name="tarifa" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <InputNumber<number>
                  placeholder="Ingrese tarifa"
                  size="middle"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  formatter={(value: number | undefined) => (value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                  parser={(value: string | undefined): number => {
                    if (!value) return 0;
                    const parsed = value.replace(/\$\s?|(,*)/g, '');
                    return isNaN(Number(parsed)) ? 0 : Number(parsed);
                  }}
                />
              </Form.Item>
            </div>

          {/* Tipo Semilla */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0
                    }}>
                      Humedad:
                    </label>
                    <Form.Item
                      name="humedad"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber<number>
                        placeholder={camposHabilitados ? 'Ingrese humedad' : 'No disponible'}
                        size="middle"
                        disabled={!camposHabilitados}
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                        precision={2}
                        formatter={(value: number | undefined) => (value ? `${value}%` : '')}
                        parser={(value: string | undefined): number => {
                          if (!value) return 0;
                          const parsed = value.replace('%', '');
                          return isNaN(Number(parsed)) ? 0 : Number(parsed);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Presentación */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Remito:
              </label>
              <Form.Item name="remito" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <Input placeholder="Ingrese remito" size="large" maxLength={13} showCount />
              </Form.Item>
            </div>

          {/* Cantidad Rollos/Módulos */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0
                    }}>
                      Cantidad Rollos/Módulos:
                    </label>
                    <Form.Item
                      name="cantidad_rollos"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber<number>
                        placeholder={camposHabilitados ? 'Ingrese cantidad' : 'No disponible'}
                        size="middle"
                        disabled={!camposHabilitados}
                        style={{ width: '100%' }}
                        min={0}
                        precision={0}
                        formatter={(value: number | undefined) => (value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                        parser={(value: string | undefined): number => {
                          if (!value) return 0;
                          const parsed = value.replace(/,/g, '');
                          return isNaN(Number(parsed)) ? 0 : Number(parsed);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Ubicación en Playa */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;
                const playaHabilitada = verificarEstadoPlaya();

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0
                    }}>
                      Ubicación en Playa:
                    </label>
                    <Form.Item
                      name="ubicacion_playa"
                      rules={[{ required: camposHabilitados && playaHabilitada, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={
                          !camposHabilitados ? 'No disponible' : !playaHabilitada ? 'Playa deshabilitada' : 'Ubicación asignada automáticamente'
                        }
                        size="middle"
                        disabled
                        maxLength={50}
                        showCount
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Calificación */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.producto !== currentValues.producto}
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find((p) => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0
                    }}>
                      Calificación:
                    </label>
                    <Form.Item
                      name="calificacion"
                      rules={[
                        {
                          required: camposHabilitados && calificacionEnabled,
                          message: 'Campo requerido',
                        },
                        {
                          pattern: /^[1-5]{3}$/,
                          message: 'Debe ingresar exactamente 3 dígitos (solo números del 1 al 5)',
                          warningOnly: !calificacionEnabled,
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        disabled={!calificacionEnabled}
                        placeholder={camposHabilitados ? 'Ingrese calificación (3 números del 1 al 5)' : 'No disponible'}
                        size="middle"
                        style={{ width: '100%' }}
                        maxLength={3}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^1-5]/g, '');
                          form.setFieldValue('calificacion', value);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* 1ra Pesada */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                1ra Pesada:
              </label>
              <Form.Item name="primera_pasada" rules={[{ required: true, message: 'Campo requerido' }]} style={{ marginBottom: 0 }}>
                <InputNumber<number>
                  placeholder="Ingrese peso"
                  size="middle"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  formatter={(value: number | undefined) => (value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                  parser={(value: string | undefined): number => {
                    if (!value) return 0;
                    const parsed = value.replace(/,/g, '');
                    return isNaN(Number(parsed)) ? 0 : Number(parsed);
                  }}
                />
              </Form.Item>
            </div>

        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${theme.colors.borderLight}`,
          flexWrap: 'wrap'
        }}>
          <Button
            onClick={() => navigate('/listar_pesaje')}
            size="middle"
            style={{
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (registroOriginal) {
                await hydrateForm(registroOriginal, true);
              } else {
                form.resetFields();
              }
            }}
            size="middle"
            style={{
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
            disabled={isSubmitting}
          >
            Restablecer
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            size="middle"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
          >
            {isSubmitting ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </div>

        {isSubmitting && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.6)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <Spin size="large" tip="Actualizando pesaje..." />
          </div>
        )}
      </Form>
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>Rollos asociados</span>
              {rollosAsignados.length > 0 && (
                <Tag color="blue" style={{ marginLeft: '6px', fontSize: '11px', padding: '2px 8px', height: '20px', lineHeight: '16px' }}>
                  {rollosAsignados.length} {rollosAsignados.length === 1 ? 'rollo' : 'rollos'}
                </Tag>
              )}
            </div>
          }
          size="small"
          style={{ 
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow,
            maxWidth: '1400px'
          }}
          styles={{
            header: {
              backgroundColor: theme.colors.primaryDark,
              color: 'white',
              borderRadius: '8px 8px 0 0',
              fontSize: '13px',
              fontWeight: 600,
              padding: '6px 12px',
              minHeight: 'auto'
            },
            body: {
              padding: '8px 12px'
            }
          }}
        >
          {rollosAsignados.length === 0 ? (
            <div style={{ 
              padding: '12px', 
              textAlign: 'center',
              color: theme.colors.textSecondary,
              fontSize: '12px'
            }}>
              No hay rollos registrados para este pesaje.
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '6px',
              padding: '4px 0'
            }}>
              {rollosAsignados.map((rollo) => (
                <Tag
                  key={rollo.id}
                  color="processing"
                  style={{
                    padding: '2px 8px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.colors.primary}`,
                    backgroundColor: '#e6f7ff',
                    color: theme.colors.primaryDark,
                    fontWeight: 500,
                    height: '22px',
                    lineHeight: '18px'
                  }}
                >
                  Rollo #{rollo.rollo_identificacion}
                </Tag>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default EditarBalanza;

